import React, { FormEvent, useEffect, useState } from 'react'
import { Form } from 'react-router-dom';
import { Alert, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { IManifest } from '../../types/manifest';
import { ICustomerLocation } from '../../types/customer';
import { IShipmentMaterial, IShipmentCommentRequest, IShipmentLight, IShipmentManifestWarningSet } from '../../types/shipment';

import { AppConfig } from '../../config/appConfig';
import { useListContext } from '../../contexts/listContext';
import { useUserContext } from '../../contexts/userContext';
import { ErrorProvider } from '../../classes/errorProvider';
import { ShipmentService } from '../../services/shipmentService';
import { IMaterialPrice } from '../../types/material';

import { Decimal } from '../../decimal';
import { Icons } from '../icons';
import Spinner from '../spinner';
import NumberField from '../shared/numberField';
import WorkingModal, { IWorkingModalProps } from '../workingModal';
import Json from '../json';

import '../../styles/shipmentMaterialsForm.scss';
import "../../styles/shared/workflowRejectDialog.scss"

export interface IShipmentMaterialsForm {
	shipment: IShipmentLight;
	materials: IShipmentMaterial[];
	isLoadingMaterials: boolean;
	manifests: IManifest[];
	isLoadingManifests: boolean;
	materialPrices: IMaterialPrice[];
	isLoadingMaterialPrices: boolean;
	manifestWarnings?: IShipmentManifestWarningSet;
	isLoadingManifestWarnings: boolean;
	customerLocations?: ICustomerLocation[];
	isLoadingCustomerLocations: boolean;
	shipmentStatus: string;
	onShipmentStatusUpdate: (status: string) => void;
	onMaterialsReloadRequest: () => void;
	onTabChangeRequest: (tab: string) => void;
}

interface IFormFields {
	recordId?: string;
	manifestId?: number;
	vendorMaterialPriceId: string;
	vendorAmount: number;
	vendorUnit: string;
	vendorPrice: number;
	vendorTotalPrice: number;
	overrideTotalPrice: boolean;
	overrideTotalPriceWith: number;
	quantity?: number;
}

interface IWarning {
	title: string;
	message: string;
}

const ShipmentMaterialsForm: React.FunctionComponent<IShipmentMaterialsForm> = ({ shipment, materials, isLoadingMaterials, manifests, isLoadingManifests, materialPrices, isLoadingMaterialPrices, manifestWarnings, isLoadingManifestWarnings, customerLocations, isLoadingCustomerLocations, shipmentStatus, onShipmentStatusUpdate, onMaterialsReloadRequest, onTabChangeRequest }) => {
	const userContext = useUserContext();
	const listContext = useListContext();
	const [shipmentMaterials, setShipmentMaterials] = useState<IShipmentMaterial[]>(materials);

	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const [editingShipmentMaterialId, setEditingShipmentMaterialId] = useState<number | undefined>();
	const [formFields, setFormFields] = useState<IFormFields | undefined>();
	const [selectedCustomerLocationId, setSelectedCustomerLocationId] = useState<number>(-1);
	const [isSaving, setIsSaving] = useState(false);
	const [warnings, setWarnings] = useState<IWarning[]>([]);
	const [showAllWarnings, setShowAllWarnings] = useState(false);
	const [showAddMaterialForm, setShowAddMaterialForm] = useState(false);
	const [newMaterial, setNewMaterial] = useState<IFormFields>();
	const [isAdding, setIsAdding] = useState(false);
	const [totalPriceField, setTotalPriceField] = useState(''); // For entering temporary NaN values in the textbox like "-"
	const [isRemovingMaterial, setIsRemovingMaterial] = useState(false);
	const [isRemovingMaterialId, setIsRemovingMaterialId] = useState<number | undefined>();
	
	const isBatchShipment = !!shipment.batchId;
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);
	const [showNoManifestWarnings, setShowNoManifestWarnings] = useState<boolean | undefined>(undefined);
	const [showRejectWorkflowDialog, setShowRejectWorkflowDialog] = useState(false);
	const [rejectWorkflowReason, setRejectWorkflowReason] = useState('-');
	const [rejectWorkflowComment, setRejectWorkflowComment] = useState('');

	const precisionFull = AppConfig.precisionDecimalsMaximum;
	const precisionRounded = AppConfig.precisionDecimalsRounded;

	useEffect(() => {
		if (materials) {
			setShipmentMaterials(materials);
			getWarnings(materials);
		}
	}, [materials])

	useEffect(() => {
		if (!isSaving) {
			setWorkingModalParams(undefined);
		}
	}, [isSaving])

	useEffect(() => {
		findAndSetCustomerLocationId();
	}, [editingShipmentMaterialId, customerLocations])

	const getCustomerLocationName = () => {
		if (customerLocations) {
			var location = customerLocations.find(cl => cl.id == selectedCustomerLocationId);
			return location ? location.locationName : undefined;
		}
		else {
			return undefined;
		}
	}

	const showQuantity = (priceId: string) => {
		const price = materialPrices.find((mp) => { return mp.id == +priceId });
		const isContainer = price?.priceByContainer ?? false;
		return isContainer;
	}

	const getPrice = (materialPriceId: string, shipmentMaterialId: string) => {
		if (!materialPriceId) return 0;
		const materialPrice = materialPrices.find((mp) => { return mp.id == +materialPriceId });
		if (!materialPrice) {
			const message = `Material price was not found at ID ${materialPriceId} for Shipent Material ID ${shipmentMaterialId}. Please contact support about this shipment.`;
			if (!errorMessage) setErrorMessage(message);
			console.error(message);
			return 0;
		}
		const price = (materialPrice?.priceByContainer ?? false) ? materialPrice?.containerPrice : materialPrice?.price;
		return price;
	}

	const getWarnings = (materials: IShipmentMaterial[]) => {
		let newWarnings: IWarning[] = [];
		let totalPlantWeight = 0;
		let totalVendorWeight = 0;

		materials.map((item) => {
			let plantPounds = convertToPounds(item.plantAmount, item.plantCreditUnitName);
			let vendorPounds = convertToPounds(item.vendorAmount, item.vendorCreditUnitName);

			totalPlantWeight = totalPlantWeight + plantPounds;
			totalVendorWeight = totalVendorWeight + vendorPounds;

			if (item.vendorAmount > item.plantAmount * 1.05 || item.vendorAmount < item.plantAmount * .95) {
				let newWarning: IWarning = {
					title: "Material " + item.id.toString(),
					message: "The difference between the customer weight and the vendor weight is greater than 5%. This shipment requires escalated approval."
				}
				newWarnings.push(newWarning);
			}
		})

		if (totalVendorWeight > totalPlantWeight * 1.05 || totalVendorWeight < totalPlantWeight * .95) {
			newWarnings.push({ title: "Total Weight", message: "The difference between the total customer weight and the total vendor weight is greater than 5%.  This shipment requires escalated approval." });
		}

		setWarnings(newWarnings);
	}

	const convertToPounds = (amount: number, unit: string) => {
		let convertedAmount = amount;
		if (!unit) {
			unit = "";
		}

		switch (unit.toLowerCase()) {
			case "gallons": {
				convertedAmount = amount * 7.46;
				break;
			}
			case "tons": {
				convertedAmount = amount * 2000;
				break;
			}
			case "gross tons": {
				convertedAmount = amount * 2240;
				break;
			}
			case "metric tons": {
				convertedAmount = amount * 2204.62;
				break;
			}
			case "kilograms": {
				convertedAmount = amount * 2.20462;
				break;
			}
			default: {
				convertedAmount = amount;
				break;
			}
		}
		return convertedAmount;
	}

	const initiateEdit = (shipmentDetailId) => {
		//Isolate the material we are editing
		const selectedShipmentDetail = shipmentMaterials.find((item) => { return item.id == shipmentDetailId });
		if (!selectedShipmentDetail) {
			setErrorMessage(`Cannot edit material with ID ${shipmentDetailId}. It was not found.`);
			console.log(`Cannot find material with ID ${shipmentDetailId}.`, shipmentMaterials);
			return;
		}

		//Set default form fields in case there are null values in the selected material
		const ff: IFormFields = {
			recordId: shipmentDetailId,
			manifestId: selectedShipmentDetail?.manifestId,
			vendorMaterialPriceId: materialPrices[0]?.id?.toString() ? materialPrices[0]?.id?.toString() : "0",
			vendorAmount: 0,
			vendorUnit: materialPrices[0]?.uomName ? materialPrices[0].uomName : "pounds",
			vendorPrice: materialPrices[0]?.price ? materialPrices[0].price : 0,
			vendorTotalPrice: 0,
			overrideTotalPrice: false,
			overrideTotalPriceWith: 0,
		}

		if (selectedShipmentDetail) {
			//Find the matching price
			const currentMaterialPrice = materialPrices.filter((mp) => { return mp.id == selectedShipmentDetail.vendorMaterialPriceId });
			if (currentMaterialPrice.length > 0) {
				const cmp = currentMaterialPrice[0];

				ff.recordId = selectedShipmentDetail.id.toString();
				ff.vendorMaterialPriceId = cmp.id.toString();
				ff.vendorAmount = selectedShipmentDetail.vendorAmount;
				ff.vendorUnit = selectedShipmentDetail.vendorUOMName.toString();
				ff.vendorPrice = (cmp.priceByContainer ?? false) ? cmp.containerPrice : cmp.price;
				ff.vendorTotalPrice = calculateVendorTotalPrice(cmp, selectedShipmentDetail);
				ff.overrideTotalPrice = selectedShipmentDetail.vendorCreditTotalOverridden;
				ff.overrideTotalPriceWith = selectedShipmentDetail.vendorCreditTotal;
				ff.quantity = selectedShipmentDetail.vendorMaterialPricePriceByContainer ? selectedShipmentDetail.quantity ?? 0 : null;
			}
		}

		setFormFields(ff);
		setTotalPriceField(ff.overrideTotalPriceWith.toString());
		setEditingShipmentMaterialId(shipmentDetailId);
	}

	const findAndSetCustomerLocationId = () => {
		if (!editingShipmentMaterialId || !customerLocations) return;
		const selectedShipmentDetail = shipmentMaterials.find((item) => { return item.id == editingShipmentMaterialId });
		if (customerLocations && selectedShipmentDetail?.materialInventoryLocation) {
			const matchingLocation = customerLocations.find(cl => cl.locationName.trim().toLowerCase() === selectedShipmentDetail.materialInventoryLocation.trim().toLowerCase());
			if (matchingLocation) {
				setSelectedCustomerLocationId(matchingLocation.id);
			} else {
				setSelectedCustomerLocationId(-1);
			}
		}
		else {
			setSelectedCustomerLocationId(-1);
		}
	}


	const calculateVendorTotalPrice = (materialPrice: IMaterialPrice, shipmentDetails: IShipmentMaterial) => {
		if (materialPrice.priceByContainer ?? false) {
			return materialPrice.containerPrice * (shipmentDetails.quantity ?? 1);
		}
		return new Decimal(shipmentDetails.vendorAmount).mul(materialPrice.price).toDecimalPlaces(precisionFull).toNumber();
	}

	const handleMaterialChangeOnEditForm = (event) => {
		const selectedId = event.target.value;
		const filteredMaterials = materialPrices.filter((item) => { return item.id == selectedId });
		if (filteredMaterials && filteredMaterials.length > 0) {
			const selectedMaterial = filteredMaterials[0];
			let newFormFields = { ...formFields };
			newFormFields.vendorMaterialPriceId = selectedMaterial.id.toString();
			newFormFields.vendorPrice = selectedMaterial.price;
			newFormFields.vendorUnit = selectedMaterial.uomName;
			newFormFields.vendorTotalPrice = new Decimal(selectedMaterial.price).mul(newFormFields.vendorAmount).toDecimalPlaces(precisionFull).toNumber();
			newFormFields.quantity = selectedMaterial.priceByContainer ? formFields?.quantity ?? 0 : null;

			// Reset manifest selection if the new material changed cannot be placed on the assigned manifest
			const selectedManifest = manifests.find(m => m.id == formFields.manifestId);
			if (selectedManifest && !selectedManifest.isHazardous && selectedManifest.isHazardous != selectedMaterial.isHazardous) {
				newFormFields.manifestId = 0;
			}

			const qualifyingManifests = manifests.filter(m => m.isHazardous || m.isHazardous == selectedMaterial.isHazardous);
			if (showNoManifestWarnings !== undefined) {
				setShowNoManifestWarnings(qualifyingManifests.length == 0);
			}

			setFormFields(newFormFields);
		}
	}

	const handleAmountChangeOnEditForm = (event) => {
		let newFormFields = { ...formFields };
		newFormFields.vendorAmount = parseFloat(event.target.value.toString());
		const materialPrice = materialPrices.find((mp) => { return mp.id.toString() == newFormFields.vendorMaterialPriceId });
		if (materialPrice?.priceByContainer != true) {
			newFormFields.vendorTotalPrice = new Decimal(parseFloat(event.target.value.toString())).mul(newFormFields.vendorPrice).toDecimalPlaces(precisionFull).toNumber();
		}
		setFormFields(newFormFields);
	}

	const handleQuantityChangeOnEditForm = (event) => {
		let newFormFields = { ...formFields };
		newFormFields.quantity = parseFloat(event.target.value.toString());
		const materialPrice = materialPrices.find((mp) => { return mp.id.toString() == newFormFields.vendorMaterialPriceId });
		if (materialPrice?.priceByContainer) {
			newFormFields.vendorTotalPrice = materialPrice.containerPrice * newFormFields?.quantity;
		}
		setFormFields(newFormFields);
	}

	const cancelEdit = () => {
		setEditingShipmentMaterialId(undefined);
	}

	const handleSubmitEdit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setErrorMessage(undefined);
		setIsSaving(true);

		if (formFields.vendorMaterialPriceId) {
			const request: IShipmentMaterial = {
				id: formFields.recordId ? parseInt(formFields.recordId) : undefined,
				manifestId: formFields.manifestId || null,
				vendorMaterialPriceId: parseInt(formFields.vendorMaterialPriceId),
				vendorAmount: formFields.vendorAmount ?? 0,
				vendorCreditTotal: formFields.overrideTotalPrice ? formFields.overrideTotalPriceWith : formFields.vendorTotalPrice,
				vendorCreditTotalAsCalculated: formFields.vendorTotalPrice,
				vendorCreditTotalOverridden: formFields.overrideTotalPrice,
				quantity: formFields.quantity,
				materialInventoryLocation: getCustomerLocationName()
			};
			ShipmentService.updateVendorsMaterialInfo(request)
				.then((response) => {
					console.log('ShipmentService.updateVendorsMaterialInfo: SUCCESS', response);
					onMaterialsReloadRequest?.();
					setEditingShipmentMaterialId(undefined);
				})
				.catch((error) => {
					console.log('ShipmentService.updateVendorsMaterialInfo: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
					window.scrollTo(0, 0);
				})
				.finally(() => {
					setIsSaving(false);
				});
		}
	}

	const initiateAdd = () => {
		if (materialPrices && materialPrices.length > 0) {
			//Set default form fields in case there are null values in the selected material
			const ff: IFormFields = {
				recordId: shipment.id.toString(),
				vendorMaterialPriceId: materialPrices[0]?.id.toString(),
				vendorAmount: 0,
				vendorUnit: materialPrices[0].uomName,
				vendorPrice: materialPrices[0].price,
				vendorTotalPrice: 0,
				overrideTotalPrice: false,
				overrideTotalPriceWith: 0
			}

			setNewMaterial(ff);
			setSelectedCustomerLocationId(-1);
			setTotalPriceField(ff.overrideTotalPriceWith.toString());
			setShowAddMaterialForm(true);
		}
		else {
			setErrorMessage("There are no active material prices set up for this customer and vendor.");
		}

	}

	const handleMaterialChangeOnAddForm = (event) => {
		const selectedId = event.target.value;
		const filteredMaterials = materialPrices.filter((item) => { return item.id == selectedId });
		if (filteredMaterials && filteredMaterials.length > 0) {
			const selectedMaterial = filteredMaterials[0];
			let newFormFields = { ...newMaterial };
			newFormFields.vendorMaterialPriceId = selectedMaterial.id.toString();
			newFormFields.vendorPrice = selectedMaterial.price;
			newFormFields.vendorUnit = selectedMaterial.uomName;
			newFormFields.vendorTotalPrice = new Decimal(selectedMaterial.price).mul(newFormFields.vendorAmount).toDecimalPlaces(precisionFull).toNumber();
			newFormFields.quantity = selectedMaterial.priceByContainer ? formFields?.quantity ?? 0 : null;

			// Reset manifest selection if the new material changed cannot be placed on the assigned manifest
			const selectedManifest = manifests.find(m => m.id == newMaterial.manifestId);
			if (selectedManifest && !selectedManifest.isHazardous && selectedManifest.isHazardous != selectedMaterial.isHazardous) {
				newFormFields.manifestId = 0;
			}

			const qualifyingManifests = manifests.filter(m => m.isHazardous || m.isHazardous == selectedMaterial.isHazardous);
			if (showNoManifestWarnings !== undefined) {
				setShowNoManifestWarnings(qualifyingManifests.length == 0);
			}

			setNewMaterial(newFormFields);
		}
	}

	const handleAmountChangeOnAddForm = (event) => {
		let newFormFields = { ...newMaterial };
		newFormFields.vendorAmount = parseFloat(event.target.value.toString());
		const materialPrice = materialPrices.find((mp) => { return mp.id.toString() == newFormFields.vendorMaterialPriceId });
		if (materialPrice?.priceByContainer != true) {
			newFormFields.vendorTotalPrice = new Decimal(parseFloat(event.target.value.toString())).mul(newFormFields.vendorPrice).toDecimalPlaces(precisionFull).toNumber();
		}
		setNewMaterial(newFormFields);
	}

	const handleQuantityChangeOnAddForm = (event) => {
		let newFormFields = { ...newMaterial };
		newFormFields.quantity = parseFloat(event.target.value.toString());
		const materialPrice = materialPrices.find((mp) => { return mp.id.toString() == newFormFields.vendorMaterialPriceId });
		if (materialPrice?.priceByContainer) {
			newFormFields.vendorTotalPrice = new Decimal(materialPrice.containerPrice).mul(newFormFields.quantity).toNumber();
		}
		setNewMaterial(newFormFields);
	}

	const cancelAdd = () => {
		//Discard any changes
		const ff: IFormFields = {
			recordId: shipment.id.toString(),
			vendorMaterialPriceId: materialPrices[0].id.toString(),
			vendorAmount: 0,
			vendorUnit: materialPrices[0].uomName,
			vendorPrice: materialPrices[0].price,
			vendorTotalPrice: 0,
			overrideTotalPrice: false,
			overrideTotalPriceWith: 0,
			manifestId: 0
		}
		setNewMaterial(ff);
		setShowAddMaterialForm(false);
	}

	const handleSubmitAdd = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (newMaterial.vendorMaterialPriceId) {
			setIsAdding(true);
			const filteredMaterials = materialPrices.filter((item) => { return item.id.toString() == newMaterial.vendorMaterialPriceId });
			if (filteredMaterials && filteredMaterials.length > 0) {
				const selectedMaterialPrice = filteredMaterials[0];
				let newShipmentMaterial: IShipmentMaterial = {
					shipmentId: shipment.id,
					vendorMaterialId: selectedMaterialPrice.materialId,
					vendorMaterialPriceId: parseInt(newMaterial.vendorMaterialPriceId),
					vendorAmount: newMaterial.vendorAmount ?? 0,
					vendorUOMID: selectedMaterialPrice.uomId,
					vendorCreditPerUnit: selectedMaterialPrice.price,
					vendorCreditTotal: newMaterial.vendorTotalPrice,
					vendorCreditTotalOverridden: newMaterial.overrideTotalPrice,
					vendorMaterialPricePriceByContainer: selectedMaterialPrice.priceByContainer,
					vendorMaterialPriceContainerId: selectedMaterialPrice.containerId,
					vendorMaterialPriceContainerPrice: selectedMaterialPrice.containerPrice,
					quantity: newMaterial.quantity,
					manifestId: newMaterial.manifestId || null,
					materialInventoryLocation: getCustomerLocationName()
				};

				if (newMaterial.overrideTotalPrice) {
					newShipmentMaterial.vendorCreditTotal = newMaterial.overrideTotalPriceWith;
					newShipmentMaterial.vendorCreditTotalOverridden = newMaterial.overrideTotalPrice;
					newShipmentMaterial.vendorCreditTotalAsCalculated = newMaterial.vendorTotalPrice;
				}

				ShipmentService.addMaterialToShipment(newShipmentMaterial)
					.then((response) => {
						console.log('ShipmentService.addMaterialToShipment: SUCCESS', response);
						onMaterialsReloadRequest?.();
					})
					.catch((error) => {
						console.log('ShipmentService.addMaterialToShipment', error);
						setErrorMessage(ErrorProvider.getError(error));
					})
					.finally(() => {
						setIsAdding(false);
						setShowAddMaterialForm(false);
					});
			}
		}
	}

	const initiateRemoveMaterial = (shipmentDetailRecordId: number | undefined) => {
		setErrorMessage(undefined);

		if (shipmentDetailRecordId) {
			setIsRemovingMaterial(true);
			setIsRemovingMaterialId(shipmentDetailRecordId);
		}
	}

	const handleCancelRemoveMaterial = () => {
		setErrorMessage(undefined);
		setIsRemovingMaterial(false);
		setIsRemovingMaterialId(undefined);
	}

	const handleRemoveMaterial = async () => {
		setErrorMessage(undefined);

		if (isRemovingMaterialId) {
			ShipmentService.removeMaterial(isRemovingMaterialId)
				.then((response) => {
					console.log('ShipmentService.removeMaterial: SUCCESS', response);
					onMaterialsReloadRequest?.();
					setErrorMessage(undefined);
					setIsRemovingMaterial(false);
					setIsRemovingMaterialId(undefined);
				})
				.catch((error) => {
					console.log('ShipmentService.removeMaterial: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				});
		}
	}

	const openRejectWorkflowDialog = () => {
		setErrorMessage(undefined);
		setRejectWorkflowReason('-');
		setRejectWorkflowComment('');
		setShowRejectWorkflowDialog(true);
	}

	const handleRejectDialogCancel = () => {
		setErrorMessage(undefined);
		setShowRejectWorkflowDialog(false);
	}

	const handleRejectDialogReject = () => {
		if (rejectWorkflowReason == '-' || !rejectWorkflowComment?.trim()) {
			// UI should block this but check anyway
			return;
		}

		setIsSaving(true);
		setShowRejectWorkflowDialog(false);

		setWorkingModalParams({
			params: {
				content:
					<div className="workflow-update">
						<div className="header">Updating Workflow:</div>
						<div className="content">
							<>Reconciling <Icons.ArrowRight height="32px" className="back"></Icons.ArrowRight> {shipmentStatus}</>
						</div>
					</div>
			}
		});

		let request: IShipmentCommentRequest = {
			reason: rejectWorkflowReason,
			comment: rejectWorkflowComment,
			isBacktrack: true
		}

		ShipmentService.setStatusReconciling(shipment?.id?.toString(), request)
			.then((response) => {
				console.log('ShipmentService.setStatusReconciling: SUCCESS', response);
				if (onShipmentStatusUpdate) {
					onShipmentStatusUpdate('Reconciling');
				}
			})
			.catch((error) => {
				console.log('ShipmentService.setStatusReconciling: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsSaving(false);
			});
	}

	const handleWorkflowReject = () => {
		openRejectWorkflowDialog();
	}

	const handleWorkflowAccept = (id: string, status: string) => {
		setErrorMessage(undefined);
		setIsSaving(true);

		setWorkingModalParams({
			params: {
				content:
					<div className="workflow-update">
						<div className="header">Updating Workflow:</div>
						<div className="content">
							<>{shipmentStatus} <Icons.ArrowRight height="32px" className="forward"></Icons.ArrowRight> {status}</>
						</div>
					</div>
			}
		});

		// This was removed from the "accept" workflow but let's leave it here in case we do need to send a comment eventually
		const request: IShipmentCommentRequest = {
			comment: rejectWorkflowComment
		}

		switch (status) {
			case "Reconciling": {
				ShipmentService.setStatusReconciling(id, request)
					.then((response) => {
						console.log('ShipmentService.setStatusReconciling: SUCCESS', response);
						onShipmentStatusUpdate?.('Reconciling');
					})
					.catch((error) => {
						console.log('ShipmentService.setStatusReconciling: ERROR', error);
						setErrorMessage(ErrorProvider.getError(error));
					})
					.finally(() => {
						setIsSaving(false);
					});
				break;
			}
			case "Escalated": {
				ShipmentService.setStatusEscalated(id, request)
					.then((response) => {
						console.log('ShipmentService.setStatusEscalated: SUCCESS', response);
						onShipmentStatusUpdate?.('Escalated');
					})
					.catch((error) => {
						console.log('ShipmentService.setStatusEscalated: ERROR', error);
						setErrorMessage(ErrorProvider.getError(error));
					})
					.finally(() => {
						setIsSaving(false);
					});
				break;
			}
			case "Reviewing": {
				ShipmentService.setStatusReviewing(id, request)
					.then((response) => {
						console.log('ShipmentService.setStatusReviewing: SUCCESS', response);
						onShipmentStatusUpdate?.('Reviewing');
					})
					.catch((error) => {
						console.log('ShipmentService.setStatusReviewing: ERROR', error);
						setErrorMessage(ErrorProvider.getError(error));
					})
					.finally(() => {
						setIsSaving(false);
					});
				break;
			}
			case "Auditing": {
				ShipmentService.setStatusAuditing(id, request)
					.then((response) => {
						console.log('ShipmentService.setStatusAuditing: SUCCESS', response);
						onShipmentStatusUpdate?.('Auditing');
					})
					.catch((error) => {
						console.log('ShipmentService.setStatusAuditing: ERROR', error);
						setErrorMessage(ErrorProvider.getError(error));
					})
					.finally(() => {
						setIsSaving(false);
					});
				break;
			}
			case "QB Entry": {
				ShipmentService.setStatusQBEntry(id, request)
					.then((response) => {
						console.log('ShipmentService.setStatusQBEntry: SUCCESS', response);
						onShipmentStatusUpdate?.('QB Entry');
					})
					.catch((error) => {
						console.log('ShipmentService.setStatusQBEntry: ERROR', error);
						setErrorMessage(ErrorProvider.getError(error));
					})
					.finally(() => {
						setIsSaving(false);
					});
				break;
			}
			case "QB Audit": {
				ShipmentService.setStatusQBAudit(id, request)
					.then((response) => {
						console.log('ShipmentService.setStatusQBAudit: SUCCESS', response);
						onShipmentStatusUpdate?.('QB Audit');
					})
					.catch((error) => {
						console.log('ShipmentService.setStatusQBAudit: ERROR', error);
						setErrorMessage(ErrorProvider.getError(error));
					})
					.finally(() => {
						setIsSaving(false);
					});
				break;
			}
			case "Complete": {
				ShipmentService.setStatusComplete(id, request)
					.then((response) => {
						console.log('ShipmentService.setStatusComplete: SUCCESS', response);
						onShipmentStatusUpdate?.('Complete');
					})
					.catch((error) => {
						console.log('ShipmentService.setStatusComplete: ERROR', error);
						setErrorMessage(ErrorProvider.getError(error));
					})
					.finally(() => {
						setIsSaving(false);
					});
				break;
			}
			default: {
				// Do nothing
				setIsSaving(false);
				break;
			}
		}
	}

	const getContainerNameById = (id: number) => {
		let container = listContext.containers.find(c => c.id == id);

		return container?.name;
	}

	const getMaterialName = (materialPrice: IMaterialPrice) => {
		let materialName = materialPrice.materialName;

		if (!materialPrice.priceByContainer) {
			materialName = `${materialName} (by ${materialPrice.uomName})`;
		}
		else {
			materialName = `${materialName} (by ${getContainerNameById(materialPrice.containerId)})`;
		}

		return materialName;
	}

	const getMaterialIdLabel = (material: IShipmentMaterial) => {
		let label = "";

		if (material.materialInventoryId) {
			label = `Inventory: ${material.materialInventoryId}`;
		}

		if (material.materialInventoryId && material.manifestNumber) {
			label = label + ' / ';
		}


		if (material.manifestNumber) {
			label = label + `Manifest: ${material.manifestNumber}`;
		}

		return label;
	}

	const areAllHazmatsOnManifests = () => {
		return (shipmentMaterials?.filter(m => m.vendorMaterialIsHazardous && !m.manifestId) ?? []).length == 0;
	}

	const isSelectedMaterialHazardous = (): boolean => {
		var material = materialPrices.find(mp => mp.id.toString() == (editingShipmentMaterialId ? formFields?.vendorMaterialPriceId : newMaterial?.vendorMaterialPriceId));
		return material ? material.isHazardous : false;
	}

	return (
		<section className="hwi-shipment-materials-form">
			<Spinner dependency={isLoadingMaterials || isLoadingMaterialPrices || isLoadingManifestWarnings || !shipmentStatus} text="Loading materials...">
				{(!isBatchShipment && !editingShipmentMaterialId && !showAddMaterialForm) &&
					<div className="workflow-controls">
						<div className="title"><strong>Status: </strong>{shipmentStatus}</div>
						<div className="actions">
							{!shipmentStatus &&
								<p><CircularProgress color="primary" size={16} /> Checking status...{"  "}</p>
							}
							{shipmentStatus?.toLowerCase() == "legacy" &&
								<Button variant="contained" color='error' onClick={() => handleWorkflowAccept(shipment.id.toString(), "Reconciling")}>Put in Reconciling Status</Button>
							}
							{shipmentStatus?.toLowerCase() == "reconciling" &&
								<>
									<Button variant="outlined" disabled={isSaving} onClick={() => initiateAdd()}>Add Row</Button>
									{(warnings == undefined || warnings?.length == 0) ? (
										<Button variant="contained" disabled={isSaving || !areAllHazmatsOnManifests() || (manifestWarnings?.hasWarnings ?? false)} onClick={() => handleWorkflowAccept(shipment.id.toString(), "Reviewing")}>Submit</Button>
									) : (
										<Button variant="contained" disabled={isSaving || !areAllHazmatsOnManifests() || (manifestWarnings?.hasWarnings ?? false)} onClick={() => handleWorkflowAccept(shipment.id.toString(), "Escalated")}>Escalate</Button>
									)}
								</>
							}
							{shipmentStatus?.toLowerCase() == "escalated" &&
								<>
									{userContext?.permissions.canApproveEscalatedShipments ? (
										<>
											<Button variant="contained" disabled={isSaving} onClick={() => handleWorkflowAccept(shipment.id.toString(), "Reviewing")}>Submit</Button>
											<Button variant="contained" color='error' disabled={isSaving} onClick={() => handleWorkflowReject()}>Reject</Button>
										</>
									) : (
										<p className="text-help text-right">
											* You do not have permission to make changes.
										</p>
									)}
								</>
							}
							{shipmentStatus?.toLowerCase() == "reviewing" &&
								<>
									{userContext?.permissions.canReviewShipments ? (
										<>
											<Button variant="contained" disabled={isSaving} onClick={() => handleWorkflowAccept(shipment.id.toString(), "Auditing")}>Approve</Button>
											<Button variant="contained" color='error' disabled={isSaving} onClick={() => handleWorkflowReject()}>Reject</Button>
										</>
									) : (
										<p className="text-help">
											* You do not have permission to make changes.
										</p>
									)}
								</>
							}
							{shipmentStatus?.toLowerCase() == "auditing" &&
								<>
									{userContext?.permissions.canCompleteShipments ? (
										<>
											<Button variant="contained" disabled={isSaving} onClick={() => handleWorkflowAccept(shipment.id.toString(), "QB Entry")}>Approve</Button>
											<Button variant="contained" color='error' disabled={isSaving} onClick={() => handleWorkflowReject()}>Reject</Button>
										</>
									) : (
										<p className="text-help">
											* You do not have permission to make changes.
										</p>
									)}
								</>
							}
							{shipmentStatus?.toLowerCase() == "qb entry" &&
								<>
									{userContext?.permissions.canQBEnter ? (
										<>
											<Button variant="contained" disabled={isSaving} onClick={() => handleWorkflowAccept(shipment.id.toString(), "QB Audit")}>Submit</Button>
											<Button variant="contained" color='error' disabled={isSaving} onClick={() => handleWorkflowReject()}>Reject</Button>
										</>
									) : (
										<p className="text-help">
											* You do not have permission to make changes.
										</p>
									)}
								</>
							}
							{shipmentStatus?.toLowerCase() == "qb audit" &&
								<>
									{userContext?.permissions.canQBAudit ? (
										<>
											<Button variant="contained" disabled={isSaving} onClick={() => handleWorkflowAccept(shipment.id.toString(), "Complete")}>Complete</Button>
											<Button variant="contained" color='error' disabled={isSaving} onClick={() => handleWorkflowReject()}>Reject</Button>
										</>
									) : (
										<p className="text-help">
											* You do not have permission to make changes.
										</p>
									)}
								</>
							}
							{shipmentStatus?.toLowerCase() == "complete" &&
								<>
									{userContext?.permissions.canUnlockFinalizedShipments ? (
										<>
											<Button variant="contained" color='error' disabled={isSaving} onClick={() => handleWorkflowReject()}>Unlock</Button>
										</>
									) : (
										<p className="text-help">
											* You do not have permission to make changes.
										</p>
									)}
								</>
							}
						</div>
					</div>
				}
				{(!showAddMaterialForm && errorMessage) &&
					<div className="error">
						<Alert className="alert-error"
							severity="error"
							action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
							{errorMessage}
						</Alert>
					</div>
				}
				{shipmentStatus?.toLowerCase() == "reconciling" && !areAllHazmatsOnManifests() && !showAddMaterialForm &&
					<div className="error">
						<Alert className="alert-error"
							severity="error">
							All hazardous materials must be assigned to a manifest before this shipment can proceed to the next workflow step.
						</Alert>
					</div>
				}
				{(!editingShipmentMaterialId && !showAddMaterialForm && warnings.length > 0 && shipmentStatus != "Complete" && shipmentStatus != "Auditing") &&
					<div className="warnings">
						<Alert className="alert-warning-main"
							severity="warning"
							action={
								<Button variant="text" color='error' onClick={() => setShowAllWarnings(!showAllWarnings)}>{showAllWarnings ? "Hide List" : "Show List"}</Button>
							}
						>
							This shipment has a total of <strong>{warnings.length} warning{warnings.length === 1 ? "" : "s"}.</strong>
						</Alert>
						{showAllWarnings &&
							<>
								{warnings.map((item, index) => (
									<Alert className="alert-warning"
										severity="warning"
										key={index}
									>
										<strong>{item.title} - </strong>{item.message}
									</Alert>
								))}
							</>
						}
					</div>
				}
				{(isBatchShipment && !editingShipmentMaterialId && !showAddMaterialForm) &&
					<div className="controls-batch">
						<Button variant="outlined" className="add-row-batch" disabled={shipmentStatus != "Reconciling" || isSaving} onClick={() => initiateAdd()}>Add Row</Button>
					</div>
				}
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					{showAddMaterialForm ? (
						<Form onSubmit={(event) => handleSubmitAdd(event)}>
							<div className="row">
								<InputLabel>Vendor Material *</InputLabel>
								<Select
									value={newMaterial.vendorMaterialPriceId}
									className="material-field"
									onChange={(event) => handleMaterialChangeOnAddForm(event)}
								>
									{materialPrices.map((item, index) => (
										<MenuItem value={item.id} key={index}><span style={{ fontWeight: 'bold', marginRight: '5px' }}>{item.materialId?.toString().padStart(4, "0")}</span> {getMaterialName(item)}</MenuItem>
									))}
								</Select>
							</div>
							<div className="row">
								<InputLabel>Location</InputLabel>
								<Select className="location-field"
									name="location"
									value={selectedCustomerLocationId}
									required
									fullWidth
									onChange={(event) => { setSelectedCustomerLocationId(event.target.value as number); }}
								>
									<MenuItem value={-1} key={-1}>None</MenuItem>
									{customerLocations?.map((item, index) => (
										<MenuItem value={item.id} key={index}>{item.locationName}</MenuItem>
									))}
								</Select>
							</div>
							<div className="row">
								<InputLabel>Weight *</InputLabel>
								<NumberField
									className="amount-field"
									required
									value={newMaterial.vendorAmount}
									precision={precisionFull}
									onChange={(event) => handleAmountChangeOnAddForm(event)}
								></NumberField>
							</div>
							{showQuantity(newMaterial.vendorMaterialPriceId) &&
								<div className="row">
									<InputLabel>Quantity *</InputLabel>
									<NumberField className="quantity-field"
										required
										value={newMaterial.quantity}
										precision={0}
										min={0}
										onChange={(event) => handleQuantityChangeOnAddForm(event)}
									></NumberField>
								</div>
							}
							<div className="row">
								<InputLabel>Price</InputLabel>
								<TextField
									className="price-field"
									disabled
									value={getPrice(newMaterial.vendorMaterialPriceId, newMaterial.recordId)}
									InputProps={{
										startAdornment: <InputAdornment position="start"><strong>$</strong></InputAdornment>,
									}}
								></TextField>
							</div>
							<div className="row">
								<InputLabel>Unit</InputLabel>
								<TextField className="unit-field" disabled name="name" value={newMaterial.vendorUnit} size="small"></TextField>
							</div>
							<div className="row">
								<InputLabel>Total</InputLabel>
								<TextField
									className="total-field"
									disabled
									value={newMaterial.vendorTotalPrice}
									InputProps={{
										startAdornment: <InputAdornment position="start"><strong>$</strong></InputAdornment>,
									}}
								></TextField>
							</div>
							<div className="row">
								<FormControlLabel
									className="waste-certification-field"
									control={<Checkbox name="overrideTotalPrice" value={newMaterial.overrideTotalPrice} checked={newMaterial.overrideTotalPrice} size="small" onChange={(event) => { setNewMaterial({ ...newMaterial, ["overrideTotalPrice"]: event.target.checked }) }}></Checkbox>}
									label="Manually override the Total amount." />
							</div>
							{newMaterial.overrideTotalPrice &&
								<div className="row">
									<InputLabel>New Total</InputLabel>
									<NumberField
										className="new-total-field"
										required
										value={totalPriceField}
										isCurrency={true}
										precision={precisionFull}
										onFocus={(event) => { event.currentTarget.select(); }}
										onChange={(event) => { setTotalPriceField(event.target.value) }}
										onBlur={
											(event) => {
												let parsedValue = parseFloat(event.target.value);
												parsedValue = isNaN(parsedValue) ? 0 : parsedValue;
												setNewMaterial({ ...newMaterial, ["overrideTotalPriceWith"]: parsedValue });
												setTotalPriceField(parsedValue.toString());
											}}
									></NumberField>
								</div>
							}
							<div className="row">
								<InputLabel>Manifest {isLoadingManifests ? " (Loading...)" : ""}</InputLabel>
								<Select
									value={newMaterial.manifestId?.toString() || "0"}
									className="manifest-field"
									onChange={(event) => { setNewMaterial({ ...newMaterial, ["manifestId"]: event.target.value == "0" ? null : parseInt(event.target.value) }) }}
									onClick={() => { setShowNoManifestWarnings(manifests.filter(m => m.isHazardous || m.isHazardous == isSelectedMaterialHazardous()).length == 0 ? true : showNoManifestWarnings) }}
									disabled={isLoadingManifests}
									readOnly={manifests.filter(m => m.isHazardous || m.isHazardous == isSelectedMaterialHazardous()).length == 0}
								>
									<MenuItem value="0">None</MenuItem>
									{manifests.filter(m => m.isHazardous || m.isHazardous == isSelectedMaterialHazardous()).map((item, index) => (
										<MenuItem value={item.id} key={index}>{item.number}</MenuItem>
									))}
								</Select>
								{showNoManifestWarnings && isSelectedMaterialHazardous() !== undefined &&
									<div className="no-manifest-warning">
										This shipment has no {isSelectedMaterialHazardous() && <strong>hazardous </strong>}manifests. You can add one on the <a href="#" onClick={() => { onTabChangeRequest?.("manifests"); }}><Icons.ViewItem fill="#272f50" height="14px"></Icons.ViewItem> Manifests tab</a>.
									</div>
								}
							</div>
							<div className="controls">
								<Button type="submit" className="save-button" variant="contained" disabled={isAdding}>{isAdding ? "Adding..." : "Add"}</Button>
								<Button variant='outlined' onClick={() => cancelAdd()}>Cancel</Button>
							</div>
							{/* <Json data={newMaterial} title="New Material"></Json> */}
						</Form>
					) : (
						<>
							{shipmentMaterials.map((item, index) => (
								<div key={index}>
									{(!editingShipmentMaterialId || editingShipmentMaterialId == item.id) &&
										<div className="material" key={index}>
											<div className="headers">
												<div className="col-location">{getMaterialIdLabel(item)}</div>
												<div className="col-amount">Amount</div>
												<div className="col-unit">Unit</div>
												<div className="col-credit">Price</div>
												<div className="col-per">Per</div>
												<div className="col-total">Total Credit</div>
												<div className="col-controls"></div>
											</div>
											<div className="plant">
												<div className="col-location"><div>Plant</div></div>
												<div className="col-material">{item.plantMaterialName}</div>
												<div className="col-amount">{item?.plantAmount == undefined ? "" : new Decimal(item.plantAmount ?? 0).toFixed(precisionFull)}</div>
												<div className="col-unit">{item.plantUOMName}</div>
												<div className="col-credit">{item?.plantAmount == undefined ? "" : new Decimal((item.plantMaterialPricePriceByContainer ?? false) ? item.plantMaterialPriceContainerPrice : item.plantCreditPerUnit ?? 0).toFixed(precisionFull)}</div>
												<div className="col-per">{((item.plantMaterialPricePriceByContainer ?? false) ? item.containerName : item.plantUOMName)}</div>
												<div className="col-total">{item?.plantCreditTotal == undefined ? "" : new Decimal(item.plantCreditTotal ?? 0).toFixed(precisionRounded)}</div>
												<div className="col-controls">
													{shipmentStatus?.toLowerCase() == "reconciling" &&
														<Button color='error' onClick={() => initiateRemoveMaterial(item.id)} disabled={isRemovingMaterial}>{isRemovingMaterial && isRemovingMaterialId == item.id ? "Removing..." : (<><Icons.RemoveItem fill="#a82525" height="16px"></Icons.RemoveItem><span>&nbsp;Remove</span></>)}</Button>
													}
												</div>
											</div>
											{/* {!editingShipmentMaterialId && */}
											<div className="vendor">
												<div className="col-location"><div>Vendor</div></div>
												<div className="col-material">{item.vendorMaterialName}</div>
												<div className="col-amount">{new Decimal(item.vendorAmount ?? 0).toFixed(precisionFull)}</div>
												<div className="col-unit">{item.vendorUOMName}</div>
												<div className="col-credit">{new Decimal((item.vendorMaterialPricePriceByContainer ?? false) ? item.vendorMaterialPriceContainerPrice : item.vendorCreditPerUnit ?? 0).toFixed(precisionFull)}</div>
												<div className="col-per">{((item.vendorMaterialPricePriceByContainer ?? false) ? item.containerName : item.vendorUOMName)}</div>
												<div className="col-total">{new Decimal(item.vendorCreditTotal ?? 0).toFixed(precisionRounded)}</div>
												<div className="col-controls">
													{shipmentStatus?.toLowerCase() == "reconciling" &&
														<Button onClick={() => initiateEdit(item.id)} ><Icons.EditItem fill="#434D76" height="16px"></Icons.EditItem>Edit</Button>
													}
												</div>
											</div>
											{/* } */}
											{item.vendorCreditTotalOverridden &&
												<p className="text-help">* This price was manually overridden.</p>
											}
										</div>
									}
								</div>
							))}
							{!shipmentMaterials?.length &&
								<Alert className="alert" severity="info">
									This shipment contains no materials.
								</Alert>
							}
							{editingShipmentMaterialId &&
								<Form onSubmit={(event) => handleSubmitEdit(event)}>
									<div className="row">
										<InputLabel>Vendor Material *</InputLabel>
										<Select
											value={formFields.vendorMaterialPriceId}
											className="material-field"
											onChange={(event) => handleMaterialChangeOnEditForm(event)}
										>
											{materialPrices.map((item, index) => (
												<MenuItem value={item.id} key={index}><span style={{ fontWeight: 'bold', marginRight: '5px' }}>{item.materialId?.toString().padStart(4, "0")}</span> {getMaterialName(item)}</MenuItem>
											))}
										</Select>
									</div>
									<div className="row">
										<InputLabel>Location</InputLabel>
										<Select className="location-field"
											name="location"
											value={selectedCustomerLocationId}
											required
											fullWidth
											onChange={(event) => { setSelectedCustomerLocationId(event.target.value as number); }}
										>
											<MenuItem value={-1} key={-1}>None</MenuItem>
											{customerLocations?.map((item, index) => (
												<MenuItem value={item.id} key={index}>{item.locationName}</MenuItem>
											))}
										</Select>
									</div>
									<div className="row">
										<InputLabel>Weight *</InputLabel>
										<NumberField className="amount-field"
											required
											value={formFields.vendorAmount}
											onChange={(event) => handleAmountChangeOnEditForm(event)}
											precision={precisionFull}
										></NumberField>
									</div>
									{showQuantity(formFields.vendorMaterialPriceId) &&
										<div className="row">
											<InputLabel>Quantity *</InputLabel>
											<NumberField className="quantity-field"
												required
												value={formFields?.quantity ?? ''}
												min={0}
												precision={0}
												onChange={(event) => handleQuantityChangeOnEditForm(event)}
											></NumberField>
										</div>
									}
									<div className="row">
										<InputLabel>Price</InputLabel>
										<TextField
											className="price-field"
											disabled
											value={getPrice(formFields.vendorMaterialPriceId, formFields.recordId)}
											InputProps={{
												startAdornment: <InputAdornment position="start"><strong>$</strong></InputAdornment>,
											}}
										></TextField>
									</div>
									<div className="row">
										<InputLabel>Unit</InputLabel>
										<TextField className="unit-field" disabled name="name" value={formFields.vendorUnit} size="small"></TextField>
									</div>
									<div className="row">
										<InputLabel>Total</InputLabel>
										<TextField
											className="total-field"
											disabled
											value={formFields.vendorTotalPrice}
											InputProps={{
												startAdornment: <InputAdornment position="start"><strong>$</strong></InputAdornment>,
											}}
										></TextField>
									</div>
									<div className="row">
										<FormControlLabel
											className="waste-certification-field"
											control={<Checkbox name="overrideTotalPrice" value={formFields.overrideTotalPrice} checked={formFields.overrideTotalPrice} size="small" onChange={(event) => { setFormFields({ ...formFields, ["overrideTotalPrice"]: event.target.checked }) }}></Checkbox>}
											label="Manually override the Total amount." />
									</div>
									{formFields.overrideTotalPrice &&
										<div className="row">
											<InputLabel>New Total</InputLabel>
											<NumberField
												isCurrency={true}
												precision={precisionFull}
												value={formFields.overrideTotalPriceWith}
												onFocus={(event) => { event.currentTarget.select(); }}
												onChange={(event) => {
													setTotalPriceField(event.target.value);
												}}
												onBlur={
													(event) => {
														let parsedValue = parseFloat(event.target.value);
														parsedValue = isNaN(parsedValue) ? 0 : parsedValue;
														setFormFields({ ...formFields, ["overrideTotalPriceWith"]: parsedValue });
														setTotalPriceField(parsedValue.toString());
													}}
											></NumberField>
										</div>
									}
									<div className="row">
										<InputLabel>Manifest {isLoadingManifests ? " (Loading...)" : ""}</InputLabel>
										<Select
											value={formFields.manifestId?.toString() || "0"}
											className="manifest-field"
											onChange={(event) => { setFormFields({ ...formFields, ["manifestId"]: event.target.value == "0" ? null : parseInt(event.target.value) }) }}
											onClick={() => { setShowNoManifestWarnings(manifests.filter(m => m.isHazardous || m.isHazardous == isSelectedMaterialHazardous()).length == 0 ? true : showNoManifestWarnings) }}
											disabled={isLoadingManifests}
											readOnly={manifests.filter(m => m.isHazardous || m.isHazardous == isSelectedMaterialHazardous()).length == 0}
										>
											<MenuItem value="0">None</MenuItem>
											{manifests.filter(m => m.isHazardous || m.isHazardous == isSelectedMaterialHazardous()).map((item, index) => (
												<MenuItem value={item.id} key={index}>{item.number}</MenuItem>
											))}
										</Select>
										{showNoManifestWarnings && isSelectedMaterialHazardous() !== undefined &&
											<div className="no-manifest-warning">
												This shipment has no {isSelectedMaterialHazardous() && <strong>hazardous </strong>}manifests. You can add one on the <a href="#" onClick={() => { onTabChangeRequest?.("manifests"); }}><Icons.ViewItem fill="#272f50" height="14px"></Icons.ViewItem> Manifests tab</a>.
											</div>
										}
									</div>
									<div className="controls">
										<Button type="submit" className="save-button" variant="contained" disabled={isSaving}>{isSaving ? "Saving..." : "Save"}</Button>
										<Button variant='outlined' disabled={isSaving} onClick={() => cancelEdit()}>Cancel</Button>
									</div>
								</Form>
							}
						</>
					)}
				</LocalizationProvider>
			</Spinner>
			<Dialog
				fullScreen={false}
				open={isRemovingMaterial}
				onClose={() => setIsRemovingMaterial(false)}
				aria-labelledby='delete-confirmation-title'
				className='delete-dialog'
				maxWidth='sm'
				fullWidth={true}>
				<DialogTitle
					id="delete-confirmation-title">
					{"Remove Material"}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						<p>Are you sure you want to remove {isRemovingMaterialId ? "Material " + isRemovingMaterialId : "this material"}? </p>
						<p>This action cannot be undone.</p>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button className='delete-cancel' onClick={() => handleRemoveMaterial()}>
						Remove
					</Button>
					<Button className='delete-ok' onClick={() => handleCancelRemoveMaterial()}>
						Cancel
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				fullScreen={false}
				open={showRejectWorkflowDialog}
				onClose={() => { /* Make them click cancel instead of just any stray clicks outside the dialog */ }}
				aria-labelledby='workflow-reject-dialog'
				className='workflow-reject-dialog'
				maxWidth='sm'
				fullWidth={true}>
				<DialogTitle className="title">
					Reject {shipmentStatus} Status
				</DialogTitle>
				<DialogContent className="content">
					<DialogContentText className="text">
						<p>Please select a reason for rejecting {shipmentStatus} status and include comments with any relevant details.</p>
					</DialogContentText>
					<div className="fields">
						<Select
							className={`reason${rejectWorkflowReason == '-' ? ' unselected' : ''}`}
							value={rejectWorkflowReason}
							onChange={(e) => setRejectWorkflowReason(e.target.value)}
						>
							<MenuItem value="-" disabled>-- Select a reason --</MenuItem>
							<MenuItem value="Volume correction needed">Volume correction needed</MenuItem>
							<MenuItem value="Material correction needed">Material correction needed</MenuItem>
							<MenuItem value="Value ($) correction needed">Value ($) correction needed</MenuItem>
							<MenuItem value="Supportive documents correction needed">Supportive documents correction needed</MenuItem>
							<MenuItem value="Duplicate correction needed">Duplicate correction needed</MenuItem>
							<MenuItem value="Other">Other</MenuItem>
						</Select>
						<TextField
							className='comment'
							placeholder="Comment (required)"
							multiline
							rows={4}
							value={rejectWorkflowComment}
							onChange={(e) => setRejectWorkflowComment(e.target.value)}
						>
						</TextField>						
					</div>
				</DialogContent>
				<DialogActions className="actions">
					<Button variant="contained" color="primary" className="reject" disabled={rejectWorkflowReason == '-' || !rejectWorkflowComment?.trim()} onClick={() => handleRejectDialogReject()}>
						Reject
					</Button>
					<Button variant="contained" color="error" className="cancel" onClick={() => handleRejectDialogCancel()}>
						Cancel
					</Button>
				</DialogActions>
			</Dialog>
			<WorkingModal params={workingModalParams?.params}></WorkingModal>
		</section>
	)
}

export default ShipmentMaterialsForm;